import MediaOverlayIconButton from '@components/MediaOverlayIconButton/MediaOverlayIconButton';
import Typography from '@components/v2/ui/Typography/Typography';
import Dialog from '@mui/material/Dialog';
import { useTheme } from '@mui/material/styles';
import { ArrowLeft, ArrowRight, CloudDownload, X } from 'lucide-react';
import { MouseEvent } from 'react';
import SwipeableViews from 'react-swipeable-views';
import LangEnum from '../../../../lang/LangEnum';
import { MediaType as MediaTypeEnum } from '../../../../types/general.types';
import { handleDownloadMedia } from '../../../../utils/downloadLink';
import { useTranslation } from '../../../translation/TranslationContext';
import ProductMediaRenderer from './ProductMediaRenderer';
import useProductDetailZoom from './hooks/useProductDetailZoom';
import { Media, ProductDetailZoomProps } from './interfaces/productDetailZoom.interfaces';
import { useProductDetailZoomStyles } from './styles/productDetailZoom.styles';
import { getMediaFileName } from './utils/getMediaFileName';

const FULL_HEIGHT_STYLE = { height: '100%' };

const ProductDetailZoom = <T extends Media>({
  open,
  title,
  medias,
  onClose,
  selectedMedia,
  focusPercentToScroll = 0,
  handleRenderOtherMediasType,
}: ProductDetailZoomProps<T>) => {
  const { translate } = useTranslation();
  const theme = useTheme();

  const {
    navigate,
    zoomAnchor,
    mobileWidth,
    isSwipeable,
    getMediaUrls,
    currentMedia,
    isZoomApplied,
    hasNavigation,
    selectedIndex,
    handleImageClick,
    setSelectedIndex,
    isSwipeDisabled,
    mediaAspectRatio,
  } = useProductDetailZoom<T>({ medias, selectedMedia, focusPercentToScroll });

  const { classes, cx } = useProductDetailZoomStyles({ mediaAspectRatio });

  const handleEmptyAreaClick = (event: MouseEvent<HTMLDivElement>) => {
    if (event.target === event.currentTarget) {
      onClose();
    }
  };

  const getMediaVariantClassName = () => {
    if (mobileWidth) {
      return classes.mobileMedia;
    }

    return isZoomApplied ? classes.zoomedMedia : classes.fittedMedia;
  };

  const renderMedia = (media?: T) => {
    if (!media) {
      return null;
    }

    if (media.type !== MediaTypeEnum.IMAGE) {
      return handleRenderOtherMediasType?.(media) ?? null;
    }

    const { mediaUrl, previewUrl } = getMediaUrls(media.url);

    return (
      <ProductMediaRenderer
        isZoom
        type={media.type}
        mediaUrl={mediaUrl}
        previewUrl={previewUrl}
        isZoomed={isZoomApplied}
        focusPercentToScroll={zoomAnchor}
        onClick={handleImageClick}
        imageProps={{ className: cx(classes.media, getMediaVariantClassName()) }}
      />
    );
  };

  const renderMediaSlot = (media?: T) => (
    <div key={media?.url} className={classes.mediaSlot} onClick={handleEmptyAreaClick}>
      {renderMedia(media)}
    </div>
  );

  return (
    <Dialog
      fullScreen
      open={open}
      onClose={onClose}
      slotProps={{ paper: { className: classes.paper } }}
    >
      {!isZoomApplied && (
        <div className={classes.header}>
          <Typography noWrap variant="h6" className={classes.headerTitle}>
            {title}
          </Typography>

          {currentMedia && (
            <MediaOverlayIconButton
              icon={CloudDownload}
              outlined={false}
              testId="product-detail-zoom-download"
              ariaLabel={translate(LangEnum.GLOBAL_DOWNLOAD)}
              onClick={() =>
                handleDownloadMedia({
                  url: currentMedia.url,
                  filename: getMediaFileName({ url: currentMedia.url, title }),
                  translate,
                })
              }
            />
          )}

          <MediaOverlayIconButton
            icon={X}
            onClick={onClose}
            testId="product-detail-zoom-close"
            ariaLabel={translate(LangEnum.GLOBAL_CLOSE)}
          />
        </div>
      )}

      <div className={classes.mediaArea} onClick={handleEmptyAreaClick}>
        {hasNavigation && (
          <MediaOverlayIconButton
            icon={ArrowLeft}
            testId="product-detail-zoom-previous"
            ariaLabel={translate(LangEnum.GLOBAL_PREVIOUS)}
            className={cx(classes.navControl, classes.navControlPrevious)}
            onClick={() => navigate(-1)}
          />
        )}

        {isSwipeable ? (
          <SwipeableViews
            resistance
            index={selectedIndex}
            axis={theme.direction === 'rtl' ? 'x-reverse' : 'x'}
            disabled={isSwipeDisabled}
            className={classes.swipeableArea}
            containerStyle={FULL_HEIGHT_STYLE}
            slideStyle={FULL_HEIGHT_STYLE}
            onChangeIndex={setSelectedIndex}
          >
            {medias?.map((media) => renderMediaSlot(media))}
          </SwipeableViews>
        ) : (
          renderMediaSlot(currentMedia)
        )}

        {hasNavigation && (
          <MediaOverlayIconButton
            icon={ArrowRight}
            testId="product-detail-zoom-next"
            ariaLabel={translate(LangEnum.GLOBAL_NEXT)}
            className={cx(classes.navControl, classes.navControlNext)}
            onClick={() => navigate(1)}
          />
        )}
      </div>

      {isZoomApplied && (
        <MediaOverlayIconButton
          icon={X}
          onClick={onClose}
          testId="product-detail-zoom-close"
          ariaLabel={translate(LangEnum.GLOBAL_CLOSE)}
          className={classes.floatingCloseControl}
        />
      )}
    </Dialog>
  );
};

export type { Media };
export default ProductDetailZoom;
